import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  validateFile,
  formatFileSize,
  uploadListingFile,
  deleteListingFile,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
} from './listingFileUtils';
import css from './EditListingFilesPanel.module.css';

/**
 * EditListingFilesPanel — file upload panel for the listing edit wizard.
 *
 * Standard wizard panel props:
 *   listing, onSubmit, submitButtonText, updateInProgress, errors, panelUpdated, ready, disabled
 *
 * Files are stored in listing.attributes.publicData.files
 */
const EditListingFilesPanel = (props) => {
  const {
    className,
    listing,
    onSubmit,
    submitButtonText,
    updateInProgress,
    errors,
    panelUpdated,
    ready,
    disabled,
  } = props;

  const intl = useIntl();
  const inputRef = useRef();

  const initialFiles = listing?.attributes?.publicData?.files || [];
  const [files, setFiles] = useState(initialFiles);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const listingId = listing?.id?.uuid;

  const handleFileChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploadError(null);
    const { valid, error: validationError } = validateFile(file);
    if (!valid) {
      setUploadError(validationError);
      return;
    }

    setUploading(true);
    try {
      const uploaded = await uploadListingFile(file, listingId);
      setFiles(prev => [...prev, uploaded]);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async key => {
    try {
      await deleteListingFile(key);
      setFiles(prev => prev.filter(f => f.key !== key));
    } catch (err) {
      setUploadError(err.message);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({ publicData: { files } });
    }
  };

  const submitInProgress = updateInProgress;
  const submitReady = panelUpdated || ready;

  return (
    <div className={classNames(css.root, className)}>
      <h1 className={css.title}>
        <FormattedMessage id="EditListingFilesPanel.title" />
      </h1>
      <p className={css.description}>
        <FormattedMessage
          id="EditListingFilesPanel.description"
          values={{ maxSize: MAX_FILE_SIZE_MB }}
        />
      </p>

      <div className={css.fileList}>
        {files.map(file => (
          <div key={file.key} className={css.fileItem}>
            <a href={file.url} target="_blank" rel="noopener noreferrer" className={css.fileLink}>
              📄 {file.name}
            </a>
            <span className={css.fileSize}>{formatFileSize(file.size)}</span>
            <button
              type="button"
              onClick={() => handleDelete(file.key)}
              className={css.deleteBtn}
              title={intl.formatMessage({ id: 'EditListingFilesPanel.delete' })}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(',')}
        className={css.hiddenInput}
        onChange={handleFileChange}
      />

      <button
        type="button"
        className={css.uploadBtn}
        disabled={uploading || disabled}
        onClick={() => inputRef.current?.click()}
      >
        {uploading
          ? intl.formatMessage({ id: 'EditListingFilesPanel.uploading' })
          : intl.formatMessage({ id: 'EditListingFilesPanel.uploadButton' })}
      </button>

      {uploadError && <p className={css.errorMsg}>{uploadError}</p>}

      <form onSubmit={handleSubmit}>
        <button
          type="submit"
          className={css.submitBtn}
          disabled={submitInProgress || uploading}
        >
          {submitReady
            ? intl.formatMessage({ id: 'EditListingFilesPanel.saved' })
            : (submitButtonText || intl.formatMessage({ id: 'EditListingFilesPanel.save' }))}
        </button>
      </form>
    </div>
  );
};

export default EditListingFilesPanel;
